// The review page's knowledge of each proposal type: how to label it, which
// fields the user may correct, and how to perform it once approved. Every
// write goes through the same endpoints the rest of the UI uses, under the
// signed-in user's own session, so validation and the activity log apply.
import { api } from './api';

export const TOOL_LABEL = {
  log_interaction: 'Log an interaction',
  move_stage: 'Move pipeline stage',
  set_meeting_date: 'Set a meeting date',
  create_task: 'Create a task',
  draft_invoice: 'Draft an invoice',
  update_invoice: 'Change an invoice',
  add_contact: 'Add a contact',
  add_internal_note: 'Add an internal note',
  draft_reply: 'Send an email reply',
  ask_submitter: 'Ask the submitter a question',
  resolve_request: 'Resolve a ticket or edit request',
  move_project_status: 'Move a project',
  assign_owner: 'Assign an account owner',
  assign_shooter: 'Assign a shooter',
  run_audit: 'Run an accessibility audit',
  queue_transfer: 'Queue a website transfer',
  overdue_reminder: 'Send an overdue reminder',
  invite_user: 'Invite a portal login',
};

export const STATUS_META = {
  pending:  { label: 'Waiting for you', tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  approved: { label: 'Approved · running', tone: 'bg-[#B0FC38]/10 text-[#B0FC38] border-[#B0FC38]/20' },
  executed: { label: 'Done', tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
  failed:   { label: 'Failed', tone: 'bg-red-500/10 text-red-300 border-red-500/20' },
  denied:   { label: 'Denied', tone: 'bg-white/[0.05] text-gray-400 border-white/[0.1]' },
  expired:  { label: 'Expired', tone: 'bg-white/[0.05] text-gray-500 border-white/[0.1]' },
};

const CHANNELS = [['in_person', 'In person'], ['phone', 'Phone'], ['email', 'Email'], ['sms', 'Text'], ['video_call', 'Video call'], ['other', 'Other']];
const ROLES = [['principal', 'Principal'], ['assistant_principal', 'Assistant principal'], ['parent_coordinator', 'Parent coordinator'], ['purchase_secretary', 'Purchasing secretary'], ['it_staff', 'IT staff'], ['superintendent', 'Superintendent'], ['other_school', 'Other (school)']];

// What the user may change before approving. Anything not listed is shown but fixed.
export const EDITABLE_FIELDS = {
  log_interaction: [
    { key: 'channel', label: 'Channel', type: 'select', options: CHANNELS },
    { key: 'direction', label: 'Direction', type: 'select', options: [['outbound', 'We reached out'], ['inbound', 'They reached out']] },
    { key: 'occurredAt', label: 'When', type: 'text', placeholder: 'YYYY-MM-DD HH:MM:SS' },
    { key: 'subject', label: 'Subject', type: 'text' },
    { key: 'summary', label: 'Summary', type: 'textarea', required: true },
    { key: 'followUpAt', label: 'Follow up on', type: 'date', hint: 'Creates a task for you that day' },
    { key: 'followUpNotes', label: 'Follow-up notes', type: 'text' },
  ],
  move_stage: [
    { key: 'summary', label: 'Summary', type: 'textarea' },
  ],
  set_meeting_date: [
    { key: 'meetingScheduledAt', label: 'Meeting date', type: 'date', required: true },
  ],
  create_task: [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'dueDate', label: 'Due', type: 'date' },
    { key: 'priority', label: 'Priority', type: 'select', options: [['low', 'Low'], ['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']] },
  ],
  draft_invoice: [
    { key: 'amountDollars', label: 'Amount ($)', type: 'number', hint: 'Leave blank for the standard website bundle' },
    { key: 'description', label: 'Line description', type: 'text' },
  ],
  // The change is confirmed in the invoice editor itself, so nothing to edit here.
  update_invoice: [],
  queue_transfer: [
    { key: 'newUrl', label: 'New address', type: 'text', required: true },
    { key: 'currentUrl', label: 'Current address', type: 'text' },
    { key: 'targetDate', label: 'Target date', type: 'date' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
  invite_user: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'text', required: true },
  ],
  run_audit: [],
  add_internal_note: [
    { key: 'body', label: 'Note (staff only)', type: 'textarea', required: true },
  ],
  draft_reply: [
    { key: 'text', label: 'Reply', type: 'textarea', required: true },
  ],
  ask_submitter: [
    { key: 'text', label: 'Message to the submitter', type: 'textarea', required: true },
    { key: 'awaitReply', label: 'Mark as Awaiting reply', type: 'checkbox' },
  ],
  resolve_request: [
    { key: 'note', label: 'Resolution note', type: 'textarea' },
  ],
  move_project_status: [
    { key: 'summary', label: 'Summary', type: 'textarea' },
  ],
  assign_owner: [],
  assign_shooter: [],
  overdue_reminder: [],
  add_contact: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'role', label: 'Role', type: 'select', options: ROLES },
    { key: 'roleTitle', label: 'Exact title', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'isPrimary', label: 'Primary contact', type: 'checkbox' },
  ],
};

// Perform an approved proposal. Returns a small result the assistant is told
// about ({ interactionId }, { taskId }, …). Throws ApiError on refusal.
export async function executeApproved(action) {
  const p = action.payload || {};
  switch (action.tool) {
    case 'log_interaction': {
      const res = await api.post(`/api/admin/accounts/${p.accountId}/interactions`, {
        channel: p.channel, direction: p.direction, occurredAt: p.occurredAt, subject: p.subject || null, summary: p.summary,
        followUpNeeded: !!(p.followUpNeeded || p.followUpAt), followUpNotes: p.followUpNotes || null, followUpAt: p.followUpAt || null,
        ...(p.contactId ? { contactId: p.contactId } : {}),
      });
      return { interactionId: res?.interaction?.id || null, taskId: res?.followUpTask?.taskId || null, link: `/admin/accounts/${p.accountId}?tab=timeline` };
    }
    case 'move_stage': {
      const res = await api.post(`/api/admin/accounts/${p.accountId}/stage`, { target: p.target, to_stage: p.target, summary: p.summary || undefined, direction: p.direction || 'outbound', structured_payload: p.structured_payload || {} });
      return { stage: res?.account?.lifecycleStage || p.target, invoiceId: res?.redirect_to_invoice || null, projectId: res?.project?.id || null, warning: res?.draft_invoice_error || null, link: `/admin/accounts/${p.accountId}` };
    }
    case 'set_meeting_date': {
      const res = await api.patch(`/api/admin/accounts/${p.accountId}`, { meetingScheduledAt: p.meetingScheduledAt || null });
      return { meetingScheduledAt: res?.account?.meetingScheduledAt ?? p.meetingScheduledAt ?? null, link: '/admin/pipeline' };
    }
    case 'create_task': {
      const res = await api.post('/api/admin/tasks', { title: p.title, description: p.description || null, dueDate: p.dueDate || null, priority: p.priority || 'normal', accountId: p.accountId || null, assigneeUserId: p.assigneeUserId || null });
      return { taskId: res?.task?.id || null, link: '/admin/tasks?scope=mine' };
    }
    case 'add_contact': {
      const res = await api.post(`/api/admin/accounts/${p.accountId}/contacts`, { name: p.name, role: p.role, roleTitle: p.roleTitle || null, email: p.email || null, phone: p.phone || null, isPrimary: !!p.isPrimary });
      return { contactId: res?.contact?.id || null, link: `/admin/accounts/${p.accountId}?tab=contacts` };
    }
    case 'add_internal_note': {
      const res = await api.post(`/api/admin/accounts/${p.accountId}/internal-notes`, { body: p.body });
      return { noteId: res?.note?.id || null, link: `/admin/accounts/${p.accountId}` };
    }
    case 'draft_reply': {
      const paragraphs = String(p.text || '').split(/\n{2,}/).map((para) => `<p>${para.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>`).join('');
      const res = await api.post(`/api/admin/communications/conversations/${p.conversationId}/reply`, { from_email: p.fromEmail, to: p.to, body_html: paragraphs, body_text: p.text, client_token: `agent-${action.id}` });
      return { messageId: res?.message?.id || null, jobStatus: res?.job?.status || null, link: `/admin/communications?conversation=${p.conversationId}` };
    }
    case 'ask_submitter': {
      const base = p.kind === 'ticket' ? 'tickets' : 'edit-requests';
      const res = await api.post(`/api/admin/${base}/${p.id}/conversation`, { text: p.text, await_reply: p.awaitReply !== false, client_token: `agent-${action.id}` });
      return { jobStatus: res?.job?.status || null, link: p.kind === 'ticket' ? '/admin/tickets' : '/admin/edit-requests' };
    }
    case 'resolve_request': {
      const base = p.kind === 'ticket' ? 'tickets' : 'edit-requests';
      await api.put(`/api/admin/${base}/${p.id}`, { status: 'resolved', ...(p.note ? { adminNotes: p.note } : {}) });
      return { status: 'resolved', link: p.kind === 'ticket' ? '/admin/tickets' : '/admin/edit-requests' };
    }
    case 'move_project_status': {
      const res = await api.post(`/api/admin/projects/${p.projectId}/status`, { target: p.target, structured_payload: p.structured_payload || {}, summary: p.summary || undefined });
      return { status: res?.project?.status || p.target, link: `/admin/projects/${p.projectId}` };
    }
    case 'assign_owner': {
      await api.post(`/api/admin/accounts/${p.accountId}/owners`, { user_id: p.userId, is_primary: !!p.isPrimary });
      return { userId: p.userId, link: `/admin/accounts/${p.accountId}` };
    }
    case 'assign_shooter': {
      const res = await api.post(`/api/admin/media/events/${p.eventId}/assign`, { assignedUserId: p.userId });
      return { status: res?.status || null, link: '/admin/media' };
    }
    case 'overdue_reminder': {
      await api.post(`/api/admin/financial/invoices/${p.invoiceId}/send-overdue-reminder`, {});
      return { sent: true, link: `/admin/invoices/${p.invoiceId}` };
    }
    case 'queue_transfer': {
      const res = await api.post('/api/admin/site-transfers', { accountId: p.accountId, newUrl: p.newUrl, currentUrl: p.currentUrl || undefined, targetDate: p.targetDate || null, notes: p.notes || null });
      return { transferId: res?.transfer?.id || null, link: '/admin/domains' };
    }
    case 'invite_user': {
      const res = await api.post('/api/admin/users', { name: p.name, email: p.email, role: 'client', accountId: p.accountId, sendInvite: true });
      return { userId: res?.user?.id || null, inviteDelivery: res?.invite?.delivery || null, link: `/admin/accounts/${p.accountId}?tab=users` };
    }
    case 'run_audit': {
      const res = await api.post(`/api/admin/accounts/${p.accountId}/audit-now`, {});
      return { auditId: res?.audit?.id || res?.id || null, link: `/admin/accounts/${p.accountId}?tab=audits` };
    }
    case 'draft_invoice':
    case 'update_invoice':
      throw new Error(`${action.tool} is completed through the invoice dialog`);
    default:
      throw new Error(`Unknown proposal type ${action.tool}`);
  }
}
